import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useListLocations } from '@/features/organization-locations';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Loader2, XIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useUpdateCampaign } from '../../api';
import type { Campaign } from '../../api/types';
import { formatCampaignMoney } from '../../utils/format-campaign-money';
import { LocationExpansionField } from '../location-expansion-field';

const schema = z.object({
  name: z.string().trim().min(1, 'Enter a campaign name.').max(255),
  dailyBudget: z
    .string()
    .refine(
      (value) =>
        value === '' ||
        (Number.isFinite(Number(value)) &&
          Number(value) >= 1 &&
          /^\d+(\.\d{1,2})?$/.test(value)),
      'Enter at least 1.00, with no more than two decimal places.'
    ),
  locationId: z.string(),
  locationExpansion: z.boolean(),
  distanceKm: z
    .number()
    .min(1, 'Enter a radius of at least 1 km.')
    .max(500, 'Enter a radius of 500 km or less.'),
});
type FormData = z.infer<typeof schema>;
type Props = {
  campaign: Campaign;
  onBack: () => void;
  onClose: () => void;
};

/** Campaign settings stay inside the existing campaign detail panel. */
export function EditCampaignPanel({ campaign, onBack, onClose }: Props) {
  const { executeAsync, isExecuting, error } = useUpdateCampaign({
    onSuccess: onBack,
  });
  const { locations, isLoading, isError, refetch } = useListLocations();
  const lifetime = Number(campaign.lifetimeBudget ?? 0) > 0;
  const audienceEditable = Boolean(campaign.metaAdSetId);
  const national =
    Array.isArray(campaign.targeting?.countries) &&
    campaign.targeting.countries.length > 0;
  const defaults = {
    name: campaign.name,
    dailyBudget: campaign.dailyBudget
      ? (Number(campaign.dailyBudget) / 100).toFixed(2)
      : '',
    locationId: campaign.locationId ?? '',
    distanceKm: Number(campaign.targeting?.distanceKm ?? 25),
    locationExpansion:
      typeof campaign.targeting?.locationExpansion === 'boolean'
        ? campaign.targeting.locationExpansion
        : true,
  };
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
  });
  const { errors, dirtyFields, isDirty } = form.formState;
  const locationId = form.watch('locationId');
  const location = locations.find((item) => item.id === locationId);
  const geoChanged = Boolean(dirtyFields.locationId || dirtyFields.distanceKm);
  const audienceChanged = geoChanged || Boolean(dirtyFields.locationExpansion);
  const invalidLocation =
    geoChanged &&
    (!location || location.latitude == null || location.longitude == null);
  const onSubmit = async (data: FormData) => {
    if (invalidLocation) return;
    if (dirtyFields.dailyBudget && !data.dailyBudget) {
      form.setError('dailyBudget', { message: 'Enter a daily budget.' });
      return;
    }
    try {
      await executeAsync({
        metaCampaignId: campaign.id,
        ...(dirtyFields.name ? { name: data.name } : {}),
        ...(dirtyFields.dailyBudget && !lifetime
          ? { dailyBudget: Math.round(Number(data.dailyBudget) * 100) }
          : {}),
        ...(audienceChanged && audienceEditable
          ? {
              ...(dirtyFields.locationId || (national && geoChanged)
                ? { locationId: data.locationId }
                : {}),
              ...(dirtyFields.distanceKm ||
              dirtyFields.locationExpansion ||
              (national && geoChanged)
                ? {
                    targeting: {
                      ...(dirtyFields.distanceKm
                        ? { distanceKm: data.distanceKm }
                        : {}),
                      ...(dirtyFields.locationExpansion
                        ? { locationExpansion: data.locationExpansion }
                        : {}),
                      ...(national && geoChanged ? { countries: [] } : {}),
                    },
                  }
                : {}),
            }
          : {}),
      });
    } catch {
      /* Keep the editor and the user's changes open for retry. */
    }
  };
  return (
    <section
      className="flex h-full min-h-0 flex-col"
      aria-label="Edit campaign"
    >
      <header className="shrink-0 border-b p-5">
        <div className="mb-4 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 text-muted-foreground"
            onClick={onBack}
            disabled={isExecuting}
          >
            <ArrowLeft aria-hidden className="size-4" />
            Back to campaign ads
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="-mr-2 size-8 text-muted-foreground"
            aria-label="Close campaign details"
            onClick={onClose}
            disabled={isExecuting}
          >
            <XIcon className="size-4" />
          </Button>
        </div>
        <h2 className="text-lg font-semibold tracking-tight">Edit Campaign</h2>
        <p className="mt-1 break-words text-sm text-muted-foreground">
          {campaign.name}
        </p>
      </header>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-5">
          <FieldGroup>
            <Field data-invalid={!!errors.name}>
              <FieldLabel htmlFor="campaign-name">Campaign Name</FieldLabel>
              <Input
                id="campaign-name"
                {...form.register('name')}
                disabled={isExecuting}
                aria-invalid={!!errors.name}
              />
              <FieldError errors={[errors.name]} />
            </Field>
            <Field data-invalid={!!errors.dailyBudget}>
              <FieldLabel htmlFor="campaign-budget">
                {lifetime
                  ? 'Lifetime budget'
                  : `Daily Budget (${campaign.currency ?? 'account currency'})`}
              </FieldLabel>
              {lifetime ? (
                <FieldDescription>
                  {formatCampaignMoney(
                    Number(campaign.lifetimeBudget),
                    campaign.currency
                  )}{' '}
                  for the campaign’s lifetime. Manage this budget in Meta Ads
                  Manager.
                </FieldDescription>
              ) : (
                <>
                  <Input
                    id="campaign-budget"
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="10.00"
                    {...form.register('dailyBudget')}
                    disabled={isExecuting}
                    aria-invalid={!!errors.dailyBudget}
                  />
                  <FieldDescription>
                    Average daily spend in your ad account’s currency.
                  </FieldDescription>
                  <FieldError errors={[errors.dailyBudget]} />
                </>
              )}
            </Field>
          </FieldGroup>
          <FieldSet disabled={isExecuting}>
            <FieldLegend>Audience location</FieldLegend>
            {!audienceEditable ? (
              <FieldDescription>
                This campaign’s audience is managed in Meta Ads Manager.
              </FieldDescription>
            ) : (
              <FieldGroup>
                {national && !audienceChanged && (
                  <Alert>
                    <AlertDescription>
                      This campaign currently targets{' '}
                      {String(campaign.targeting?.countries)}. Changing the
                      location or radius switches it to the area around a
                      branch.
                    </AlertDescription>
                  </Alert>
                )}
                <Field>
                  <FieldLabel htmlFor="campaign-location">
                    Business location
                  </FieldLabel>
                  <Controller
                    control={form.control}
                    name="locationId"
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={isLoading || isError || isExecuting}
                      >
                        <SelectTrigger id="campaign-location">
                          <SelectValue
                            placeholder={
                              isLoading
                                ? 'Loading locations…'
                                : 'Choose a location'
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {locations.map((item) => (
                              <SelectItem
                                key={item.id}
                                value={item.id}
                                disabled={
                                  item.latitude == null ||
                                  item.longitude == null
                                }
                              >
                                {item.name || item.city}
                                {item.latitude == null || item.longitude == null
                                  ? ' — address needed'
                                  : ''}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {isError ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void refetch()}
                    >
                      Retry locations
                    </Button>
                  ) : (
                    <FieldDescription>
                      {location
                        ? [location.addressLine1, location.city]
                            .filter(Boolean)
                            .join(', ')
                        : 'Choose one of your saved business locations.'}{' '}
                      {locations.some(
                        (item) =>
                          item.latitude == null || item.longitude == null
                      ) &&
                        ' Locations without a mapped address need to be updated in Organisation settings.'}
                    </FieldDescription>
                  )}
                </Field>
                <Field data-invalid={!!errors.distanceKm}>
                  <FieldLabel htmlFor="campaign-radius">Radius (km)</FieldLabel>
                  <Input
                    id="campaign-radius"
                    type="number"
                    min="1"
                    max="500"
                    step="1"
                    {...form.register('distanceKm', { valueAsNumber: true })}
                    aria-invalid={!!errors.distanceKm}
                  />
                  <FieldError errors={[errors.distanceKm]} />
                </Field>
                <Controller
                  control={form.control}
                  name="locationExpansion"
                  render={({ field }) => (
                    <LocationExpansionField
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isExecuting}
                      legacyDefault={
                        typeof campaign.targeting?.locationExpansion !==
                        'boolean'
                      }
                    />
                  )}
                />
                {invalidLocation && (
                  <FieldError>
                    Choose a location with a mapped address before saving.
                  </FieldError>
                )}
                {audienceChanged && (
                  <Alert>
                    <AlertDescription>
                      Saving updates the audience on Meta. Delivery may adjust
                      while Meta learns who responds.
                    </AlertDescription>
                  </Alert>
                )}
              </FieldGroup>
            )}
          </FieldSet>
          {(!audienceEditable || lifetime) && (
            <Button variant="link" asChild>
              <a
                href="https://adsmanager.facebook.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Meta Ads Manager ↗
              </a>
            </Button>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}
        </div>
        <footer className="flex shrink-0 items-center justify-end gap-2 border-t bg-background p-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={isExecuting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!isDirty || isExecuting || invalidLocation}
          >
            {isExecuting && <Loader2 className="animate-spin" />}
            {isExecuting ? 'Saving…' : 'Save Changes'}
          </Button>
        </footer>
      </form>
    </section>
  );
}
